"use client";

import React, { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Clock, ShieldCheck, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
export default function RequestService() {
  const shouldReduceMotion = useReducedMotion();
  useEffect(() => {
    const cssHref = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/external/work_request_embed.css";
    if (!document.querySelector(`link[href="${cssHref}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.media = "screen";
      link.href = cssHref;
      document.head.appendChild(link);
    }
    const scriptSrc = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/static_link/work_request_embed_snippet.js";
    const script = document.createElement("script");
    script.src = scriptSrc;
    script.setAttribute("clienthub_id", "476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587");
    script.setAttribute("form_url", "https://clienthub.getjobber.com/client_hubs/476c5fe2-f16a-4108-ad33-1763e02c3dad/public/work_request/embedded_work_request_form?form_id=4972587");
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  return <section id="request-service" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.35,
        ease: "easeOut"
      }} className="max-w-3xl mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-bold tracking-wider uppercase mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            Direct Local Dispatch • 24/7 Availability
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default">
            Request emergency biohazard dispatch
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Tell our local crew what happened, or speak with an on-duty supervisor right now. We arrive in unmarked utility vehicles with certified decontamination equipment, maintaining total privacy for your property and family.
          </p>
        </motion.div>

        {/* Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Intake Form Column */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.1
        }} transition={{
          duration: 0.35,
          delay: 0.05,
          ease: "easeOut"
        }} className="lg:col-span-7">
            <Card className="border border-border bg-card shadow-sm rounded-lg p-6 sm:p-8">
              <div id="476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587" />
            </Card>
          </motion.div>

          {/* 24/7 Direct Response Card Column */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.1
        }} transition={{
          duration: 0.35,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5 space-y-6">
            {/* Direct Response Card */}
            <Card className="border border-border bg-secondary text-secondary-foreground shadow-md rounded-lg overflow-hidden">
              <CardHeader className="p-6 sm:p-7 border-b border-white/10">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded bg-white/10 text-white text-xs font-semibold uppercase tracking-wider w-fit mb-2">
                  <Clock className="size-3.5" />
                  Active 24/7/365 Hotline
                </div>
                <CardTitle className="text-xl sm:text-2xl font-bold font-default text-white">
                  Immediate supervisor access
                </CardTitle>
                <CardDescription className="text-white/80 text-sm leading-relaxed mt-1">
                  You will speak directly with an active local technician or field commander, never an off-site answering service or third-party call center.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-6 sm:p-7 space-y-6">
                <div className="rounded-md bg-black/20 p-4 border border-white/10">
                  <span className="text-xs uppercase tracking-wider font-semibold text-white/70 block mb-1">
                    Emergency Dispatch Number
                  </span>
                  <a href="tel:855-435-6631" className="text-2xl sm:text-3xl font-extrabold text-white hover:text-accent transition-colors font-mono tracking-tight inline-flex items-center gap-2">
                    <PhoneCall className="size-6 text-accent shrink-0" />
                    855-435-6631
                  </a>
                  <p className="text-xs text-white/70 mt-2">
                    Direct crew coordination across Maryland, Washington DC, and Northern Virginia.
                  </p>
                </div>

                <div className="space-y-4 text-sm text-white/90">
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 rounded bg-white/10 text-white shrink-0 mt-0.5">
                      <Truck className="size-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Discreet, unmarked vehicles</p>
                      <p className="text-xs text-white/70 mt-0.5">
                        Our technicians arrive in plain utility vans to protect your privacy and ensure sensitive property confidentiality.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-1.5 rounded bg-white/10 text-white shrink-0 mt-0.5">
                      <ShieldCheck className="size-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Certified decontamination protocol</p>
                      <p className="text-xs text-white/70 mt-0.5">
                        Full OSHA, EPA, and state compliant medical waste disposal with chain-of-custody documentation for insurance claims.
                      </p>
                    </div>
                  </div>
                </div>

                <Button asChild className="w-full bg-white text-secondary font-bold hover:bg-white/90 h-11 text-sm shadow-xs">
                  <a href="tel:855-435-6631" className="inline-flex items-center justify-center gap-2">
                    <PhoneCall className="size-4" />
                    Tap to call 855-435-6631
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* Insurance & Regional Coverage Note */}
            <div className="border border-border bg-muted text-muted-foreground rounded-lg p-5 text-xs space-y-2">
              <p className="font-semibold text-foreground text-sm">Direct insurance billing & coordination</p>
              <p className="leading-relaxed">
                We work directly with homeowner and commercial insurance adjusters throughout Maryland, DC, and Northern Virginia. Our itemized reporting ensures zero administrative delay during hazardous waste remediation.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}